import {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@common-web/components/ui/table';
import {
  FEATURE_AREAS,
  FEATURE_REQUIREMENTS,
  roleSatisfiesFeature,
  type FeatureRequirement,
} from '@common/featureRequirements';
import {CheckCircle2, Loader2, XCircle, X} from 'lucide-react';

/**
 * Read-only requirements matrix: which permission keys each key page / core
 * function needs. Selecting a role adds a pass/fail column computed from the
 * role's DB permission edges — the QA view for verifying role parity.
 */
export function FeatureRequirementsList() {
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  // Requires `roles.manage`; a `permissions.manage`-only viewer gets a 403 and
  // the role selector is hidden (the matrix itself is still useful read-only).
  const rolesQuery = trpc.internal.roles.list.useQuery(
    {page: 1, pageSize: 200, isDeprecated: false},
    {retry: false},
  );

  const roleQuery = trpc.internal.roles.get.useQuery(
    {id: selectedRoleId ?? ''},
    {enabled: !!selectedRoleId, retry: false},
  );

  // Permission key → row id/name, for linking badge chips to /permissions/:id.
  // Requires `permissions.manage`; without it chips render as plain badges.
  const permissionsQuery = trpc.internal.permissions.list.useQuery(
    {page: 1, pageSize: 200},
    {retry: false},
  );

  const permissionByKey = useMemo(() => {
    const map = new Map<string, {id: string; name: string}>();
    for (const p of permissionsQuery.data?.permissions ?? []) {
      map.set(p.key, {id: p.id, name: p.name});
    }
    return map;
  }, [permissionsQuery.data]);

  const selectedRolePermissions = useMemo(() => {
    if (!selectedRoleId || !roleQuery.data) return null;
    return new Set(roleQuery.data.permissions.map((rp) => rp.permission.key));
  }, [selectedRoleId, roleQuery.data]);

  const selectedRoleName = selectedRoleId
    ? rolesQuery.data?.roles.find((r) => r.id === selectedRoleId)?.name
    : undefined;

  const featuresByArea = useMemo(
    () =>
      FEATURE_AREAS.map((area) => ({
        area,
        features: FEATURE_REQUIREMENTS.filter((f) => f.area === area),
      })).filter((group) => group.features.length > 0),
    [],
  );

  return (
    <div className='px-8 py-6 max-w-7xl mx-auto w-full flex flex-col gap-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <p className='text-sm text-brand-gray max-w-2xl'>
          What each key page or core function requires. Pick a role to check what it can reach —
          useful when editing role permissions or verifying access during testing.
        </p>
        {rolesQuery.data && (
          <div className='flex items-center gap-2'>
            {roleQuery.isFetching && (
              <Loader2 className='h-4 w-4 animate-spin text-brand-gray' />
            )}
            <Select
              value={selectedRoleId ?? undefined}
              onValueChange={(value) => setSelectedRoleId(value)}
            >
              <SelectTrigger className='w-64'>
                <SelectValue placeholder='Check access for a role…' />
              </SelectTrigger>
              <SelectContent>
                {rolesQuery.data.roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedRoleId && (
              <Button
                variant='ghost'
                size='icon'
                onClick={() => setSelectedRoleId(null)}
                aria-label='Clear selected role'
              >
                <X className='h-4 w-4' />
              </Button>
            )}
          </div>
        )}
      </div>

      {featuresByArea.map(({area, features}) => (
        <Card key={area} className='border-gray-200 shadow-sm'>
          <CardHeader className='border-b bg-gray-50/60'>
            <CardTitle className='text-base font-semibold text-brand-navy'>{area}</CardTitle>
          </CardHeader>
          <CardContent className='p-0'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='w-[34%]'>Feature</TableHead>
                  <TableHead>Required permissions</TableHead>
                  {selectedRolePermissions && (
                    <TableHead className='w-44'>{selectedRoleName ?? 'Selected role'}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {features.map((feature) => (
                  <TableRow key={feature.key}>
                    <TableCell className='align-top'>
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-sm font-medium text-brand-navy'>{feature.name}</span>
                        <span className='text-xs text-brand-gray'>{feature.description}</span>
                      </div>
                    </TableCell>
                    <TableCell className='align-top'>
                      <RequirementChips feature={feature} permissionByKey={permissionByKey} />
                    </TableCell>
                    {selectedRolePermissions && (
                      <TableCell className='align-top'>
                        <RoleAccessCell
                          feature={feature}
                          rolePermissions={selectedRolePermissions}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PermissionChip({
  permissionKey,
  permissionByKey,
}: {
  permissionKey: string;
  permissionByKey: Map<string, {id: string; name: string}>;
}) {
  const match = permissionByKey.get(permissionKey);
  const badge = (
    <Badge variant='outline' className='font-mono text-[11px] font-normal'>
      {permissionKey}
    </Badge>
  );
  if (!match) return badge;
  return (
    <Link to={`/permissions/${match.id}`} className='hover:opacity-70' title={match.name}>
      {badge}
    </Link>
  );
}

function RequirementChips({
  feature,
  permissionByKey,
}: {
  feature: FeatureRequirement;
  permissionByKey: Map<string, {id: string; name: string}>;
}) {
  const hasAnyRequirement =
    feature.requiredPermissions.length > 0 || (feature.anyOfPermissions?.length ?? 0) > 0;

  return (
    <div className='flex flex-col gap-1.5'>
      {feature.requiredPermissions.length > 0 && (
        <div className='flex flex-wrap items-center gap-1'>
          {feature.requiredPermissions.map((key) => (
            <PermissionChip key={key} permissionKey={key} permissionByKey={permissionByKey} />
          ))}
        </div>
      )}
      {feature.anyOfPermissions && feature.anyOfPermissions.length > 0 && (
        <div className='flex flex-wrap items-center gap-1'>
          <span className='text-[11px] uppercase tracking-wide text-brand-gray'>Any of</span>
          {feature.anyOfPermissions.map((key) => (
            <PermissionChip key={key} permissionKey={key} permissionByKey={permissionByKey} />
          ))}
        </div>
      )}
      {!hasAnyRequirement && (
        <span className='text-xs text-brand-gray italic'>No permission required</span>
      )}
      {feature.profileNote && (
        <span className='text-xs text-brand-gray'>{feature.profileNote}</span>
      )}
    </div>
  );
}

function RoleAccessCell({
  feature,
  rolePermissions,
}: {
  feature: FeatureRequirement;
  rolePermissions: ReadonlySet<string>;
}) {
  const satisfied = roleSatisfiesFeature(feature, rolePermissions);

  if (satisfied) {
    return (
      <div className='flex items-center gap-1.5 text-sm text-green-700'>
        <CheckCircle2 className='h-4 w-4 shrink-0' />
        Can access
      </div>
    );
  }

  const missingRequired = feature.requiredPermissions.filter((key) => !rolePermissions.has(key));
  const missingAnyOf =
    feature.anyOfPermissions && !feature.anyOfPermissions.some((key) => rolePermissions.has(key))
      ? feature.anyOfPermissions
      : [];

  return (
    <div className='flex flex-col gap-1'>
      <div className='flex items-center gap-1.5 text-sm text-red-700'>
        <XCircle className='h-4 w-4 shrink-0' />
        Missing permissions
      </div>
      <div className='flex flex-col gap-0.5'>
        {missingRequired.map((key) => (
          <span key={key} className='font-mono text-[11px] text-brand-gray'>
            {key}
          </span>
        ))}
        {missingAnyOf.length > 0 && (
          <span className='font-mono text-[11px] text-brand-gray'>
            any of: {missingAnyOf.join(', ')}
          </span>
        )}
      </div>
    </div>
  );
}
